import { queryOptions } from "@tanstack/react-query";
import { call, send } from "./api";
import type { NotChangeable, WaitingReason } from "./shipments";

// Entering an order in the portal (docs/partner-portal.md §14, docs/api.md §9.26): what the form needs, the
// customer's address book, the form as the portal keeps it (also the concept on the server), and the form as WLX
// takes it. No price anywhere (P7).

// ─── What WLX gives the form ───────────────────────────────────────────────────────────────────

/**
 * As WLX has them (services/api partner/orderGoods.ts): an IBC in four sizes and a block pallet of 0,9 m (20-10-2026),
 * a block pallet of 0,6 m and a big bag (02-11-2026).
 */
export const CARRIERS = [
  "europallet",
  "block_pallet",
  "block_pallet_06",
  "block_pallet_09",
  "ibc_02",
  "ibc_04",
  "ibc",
  "ibc_06",
  "big_bag",
  "roll_container",
  "box",
  "other",
] as const;
export type Carrier = (typeof CARRIERS)[number];

export const STOP_REQUIREMENTS = ["tail_lift", "rigid_with_tail_lift", "no_rear_tail_lift"] as const;
export type StopRequirement = (typeof STOP_REQUIREMENTS)[number];

export interface OrderFormSettings {
  canEnterOrders: boolean;
  company: { name: string; city: string | null };
  cutoffTime: string | null;
  /** The code says which is ADR: with it ticked, the form asks the ADR code (20-10-2026). */
  requirements: { id: string; code?: string; name: string; isDefault: boolean }[];
  /** The ADR codes to pick from; absent from an API before 20-10-2026. */
  adrCodes?: AdrCode[];
  stopRequirements: StopRequirement[];
  carriers: { carrier: Carrier; loadingMeters: number | null; palletPlace: boolean }[];
  /** The customer's own carriers, each counted as a fixed one (§19, 22-10-2026); absent from an older API. */
  ownCarriers?: OwnCarrier[];
}

/** A carrier of the customer's own: its name, and the fixed carrier it counts as. */
export interface OwnCarrier {
  id: string;
  name: string;
  carrier: Carrier;
}

export interface AdrCode {
  code: string;
  description: string;
  hazardClass: string | null;
  classificationCode: string | null;
  packingGroup: string | null;
  /** Per packing group the transport category (the 1000-point rule); absent from an API before it. */
  variants?: { packingGroup: string | null; transportCategory: number; tunnelCode: string | null }[];
}

/** A line of dangerous goods while it is typed: the code from the list, its packing group, how much in kg or litres. */
export interface AdrLineState {
  code: string;
  packingGroup: string;
  quantity: string;
  unit: "kg" | "l";
}

export const emptyAdrLine = (): AdrLineState => ({ code: "", packingGroup: "", quantity: "", unit: "kg" });

/** The requirement ADR among the form's, or null. */
export function adrRequirementId(settings: OrderFormSettings | undefined): string | null {
  return settings?.requirements.find((requirement) => requirement.code === "ADR")?.id ?? null;
}

/** The ADR lines to send: those with a code, only with the requirement ADR ticked; without it none (clears them). */
export function adrLinesToSend(requirementIds: string[], lines: AdrLineState[] | undefined, settings: OrderFormSettings | undefined) {
  const adr = adrRequirementId(settings);
  if (adr === null || !requirementIds.includes(adr)) return [];
  return (lines ?? [])
    .filter((line) => line.code !== "")
    .map((line) => ({ code: line.code, packingGroup: line.packingGroup || null, quantity: readDecimal(line.quantity), unit: line.unit }));
}

export const orderFormQuery = queryOptions({
  queryKey: ["partner", "order-form"],
  queryFn: () => call<OrderFormSettings>("GET", "/partner/order-form"),
  staleTime: 5 * 60_000,
});

export interface BookEntry {
  id: string;
  name: string;
  street: string | null;
  houseNumber: string | null;
  houseNumberAddition: string | null;
  postalCode: string | null;
  city: string;
  countryCode: string;
  uses: number;
  lastUsed: string | null;
  hours: { status: "known" | "always" | "unknown"; days: Record<string, string> };
}

export const addressesQuery = queryOptions({
  queryKey: ["partner", "addresses"],
  queryFn: () => call<BookEntry[]>("GET", "/partner/addresses"),
  staleTime: 5 * 60_000,
});

// ─── The form as the portal keeps it ───────────────────────────────────────────────────────────

export interface AddressState {
  name: string;
  street: string;
  houseNumber: string;
  houseNumberAddition: string;
  postalCode: string;
  city: string;
  countryCode: string;
}

export interface StopState {
  /** One of "Uw adressen", by id; null with a typed address, or nothing chosen yet. */
  locationId: string | null;
  /** The address as shown: of the book entry, or typed. */
  address: AddressState;
  /** Typing a new address (the fields open), not choosing from the book. */
  typing: boolean;
  date: string;
  from: string;
  to: string;
  /** An end before the start runs to the next day: the night of 22:00 – 06:00 (P82); absent from an older concept. */
  toNextDay?: boolean;
  reference: string;
  instruction: string;
  /** Who to ask for there, and on what number (20-10-2026). */
  contactName: string;
  contactPhone: string;
  requirement: StopRequirement | "";
}

export interface LineState {
  count: string;
  /** With a carrier of the customer's own: the fixed carrier it counts as. */
  carrier: Carrier;
  /** A carrier of the customer's own (§19); absent from an older concept. */
  ownCarrierId?: string | null;
  description: string;
  weightKg: string;
  loadingMeters: string;
}

export interface ConsignmentState {
  key: string;
  load: StopState;
  unload: StopState;
  lines: LineState[];
  requirementIds: string[];
  /** The cubic metres, and the ADR lines with the requirement ADR (20-10-2026); absent from an older concept. */
  volumeM3?: string;
  adrLines?: AdrLineState[];
  /** Null: the company, as Willems knows it. */
  sender: AddressState | null;
  /** Null: the same as the unloading address. */
  consignee: AddressState | null;
  /** The packing slips added to it (§17), already with WLX; empty in a concept from before they existed. */
  packingSlips?: PackingSlip[];
}

/** A pdf added to a consignment of the order being typed: kept by WLX until the order is sent. */
export interface PackingSlip {
  id: string;
  name: string;
  size: number;
}

/** How many a consignment takes, and how large one is: as WLX checks it. */
export const PACKING_SLIPS_MAX = 2;
export const PACKING_SLIP_MAX_BYTES = 10 * 1024 * 1024;

export interface FormState {
  version: 1;
  customerReference: string;
  consignments: ConsignmentState[];
}

export const STEPS = ["stops", "goods", "documents", "check"] as const;
export type Step = (typeof STEPS)[number];

/** What the concept on the server holds: the form and the step it was on. */
export interface DraftPayload {
  form: FormState;
  step: Step;
}

export function emptyAddress(countryCode = "NL"): AddressState {
  return { name: "", street: "", houseNumber: "", houseNumberAddition: "", postalCode: "", city: "", countryCode };
}

export function emptyStop(): StopState {
  return { locationId: null, address: emptyAddress(), typing: false, date: "", from: "", to: "", reference: "", instruction: "", contactName: "", contactPhone: "", requirement: "" };
}

export function emptyLine(): LineState {
  return { count: "1", carrier: "europallet", description: "", weightKg: "", loadingMeters: "" };
}

let keys = 0;
export function newKey(): string {
  keys += 1;
  return `c${Date.now().toString(36)}${keys}`;
}

export function emptyConsignment(defaults: string[]): ConsignmentState {
  return { key: newKey(), load: emptyStop(), unload: emptyStop(), lines: [emptyLine()], requirementIds: [...defaults], sender: null, consignee: null, packingSlips: [] };
}

export function emptyForm(defaults: string[]): FormState {
  return { version: 1, customerReference: "", consignments: [emptyConsignment(defaults)] };
}

/** Another consignment in the same order: loaded where the last one is loaded, to another address (handoff §4.4). */
export function nextConsignment(form: FormState, defaults: string[]): ConsignmentState {
  const last = form.consignments[form.consignments.length - 1];
  const next = emptyConsignment(defaults);
  if (last) next.load = { ...last.load, reference: "", instruction: "" };
  return next;
}

/**
 * A form read back from a concept: what does not look like one is no concept. A concept saved before a field
 * existed gets it empty (contactName and contactPhone since 20-10-2026), so nothing reads a field that is not there.
 */
export function readDraft(payload: unknown): DraftPayload | null {
  if (!payload || typeof payload !== "object") return null;
  const draft = payload as Partial<DraftPayload>;
  if (!draft.form || draft.form.version !== 1 || !Array.isArray(draft.form.consignments) || draft.form.consignments.length === 0) return null;
  const consignments = draft.form.consignments.map((c) => ({
    ...c,
    load: { ...emptyStop(), ...c.load, address: { ...emptyAddress(), ...c.load?.address } },
    unload: { ...emptyStop(), ...c.unload, address: { ...emptyAddress(), ...c.unload?.address } },
  }));
  return { form: { ...draft.form, consignments }, step: STEPS.includes(draft.step as Step) ? (draft.step as Step) : "stops" };
}

// ─── Reading what was typed ────────────────────────────────────────────────────────────────────

/** A whole number from a field, or null: "1.200" and "1200" are the same, as the Netherlands writes them. */
export function readInt(text: string): number | null {
  const clean = text.replace(/[.\s]/g, "");
  if (!/^\d+$/.test(clean)) return null;
  return Number(clean);
}

/** A decimal from a field, comma or point: "2,4" → 2.4. */
export function readDecimal(text: string): number | null {
  const clean = text.trim().replace(",", ".");
  if (!/^\d+(\.\d+)?$/.test(clean)) return null;
  return Number(clean);
}

export function addressComplete(address: AddressState): boolean {
  return address.name.trim().length >= 2 && address.city.trim().length > 0 && address.countryCode.length === 2;
}

export function stopPlaced(stop: StopState): boolean {
  return stop.locationId !== null || (stop.typing && addressComplete(stop.address));
}

/** Whether a window ends before it starts on the clock: 13:00 – 12:00. Equal times are one moment. */
export function endsBeforeStart(stop: Pick<StopState, "from" | "to">): boolean {
  return stop.from !== "" && stop.to !== "" && stop.to < stop.from;
}

/**
 * A window that ends before it starts and is not ticked as running to the next day (P82, Mark 26-10-2026: "13:00 -
 * 12:00 mag ik invoeren, dat kan niet"). The night of 22:00 – 06:00 is ticked; anything else of the kind is a typing
 * error, and the step does not go on until it is put right — WLX refuses it all the same.
 */
export function reversedWindow(stop: Pick<StopState, "from" | "to" | "toNextDay">): boolean {
  return endsBeforeStart(stop) && !stop.toNextDay;
}

function stopComplete(stop: StopState): boolean {
  return stopPlaced(stop) && Boolean(stop.date) && !reversedWindow(stop);
}

/** What keeps a stop from being sent, said at the stop itself; the reversed window says itself (WindowNextDay). */
export function stopGaps(stop: StopState, earliest?: string): { address: boolean; date: boolean; beforeLoad: boolean } {
  return {
    address: !stopPlaced(stop),
    date: !stop.date,
    beforeLoad: Boolean(stop.date && earliest && stop.date < earliest),
  };
}

/**
 * A line is complete with a number and its loading metres: worked out for a pallet or an IBC, typed for the rest (P19,
 * hoofdplanner 09-10-2026: WLX does not guess them). A description is not required (P24).
 */
export function lineComplete(line: LineState, settings: OrderFormSettings | undefined): boolean {
  const count = readInt(line.count);
  return count !== null && count >= 1 && (lineMeters(line, settings) ?? 0) > 0;
}

/** Whether a step has what sending needs. The fourth only shows what the other three made. */
export function stepComplete(form: FormState, step: Step, settings: OrderFormSettings | undefined): boolean {
  switch (step) {
    case "stops":
      return (
        form.customerReference.trim().length > 0 &&
        form.consignments.every((c) => stopComplete(c.load) && stopComplete(c.unload) && c.unload.date >= c.load.date)
      );
    case "goods":
      return form.consignments.every((c) => c.lines.length > 0 && c.lines.every((line) => lineComplete(line, settings)));
    case "documents":
      return form.consignments.every((c) => (c.sender === null || addressComplete(c.sender)) && (c.consignee === null || addressComplete(c.consignee)));
    case "check":
      return true;
  }
}

/** Whether anything is typed in a step yet: an untouched step reads "nog in te vullen", not as missing. */
export function stepTouched(form: FormState, step: Step): boolean {
  switch (step) {
    case "stops":
      return form.customerReference.trim() !== "" || form.consignments.some((c) => stopPlaced(c.load) || stopPlaced(c.unload) || c.load.date !== "");
    case "goods":
      return form.consignments.some((c) => c.lines.some((line) => line.description.trim() !== "" || line.weightKg !== "" || line.loadingMeters !== ""));
    case "documents":
      return form.consignments.some((c) => c.sender !== null || c.consignee !== null);
    case "check":
      return false;
  }
}

// ─── The lines added up, as WLX will (services/api modules/partner/orderGoods.ts) ─────────────

export interface Totals {
  pallets: number;
  weightKg: number | null;
  loadingMeters: number | null;
}

/** The own carrier of a line, while the customer still has it; without, the line is its fixed carrier. */
export function ownCarrierOf(line: Pick<LineState, "ownCarrierId">, settings: OrderFormSettings | undefined): OwnCarrier | null {
  if (!line.ownCarrierId) return null;
  return settings?.ownCarriers?.find((own) => own.id === line.ownCarrierId) ?? null;
}

/** The rule of a line's carrier: of the fixed carrier an own one counts as, as the customer's list says now. */
export function carrierRule(line: Pick<LineState, "carrier" | "ownCarrierId">, settings: OrderFormSettings | undefined) {
  const carrier = ownCarrierOf(line, settings)?.carrier ?? line.carrier;
  return settings?.carriers.find((rule) => rule.carrier === carrier);
}

/** What the carrier field shows: an own carrier as "own:<id>", a fixed one by its code. */
export function carrierValue(line: Pick<LineState, "carrier" | "ownCarrierId">, settings: OrderFormSettings | undefined): string {
  const own = ownCarrierOf(line, settings);
  return own ? `own:${own.id}` : line.carrier;
}

/** A choice in the carrier field, as the line takes it. */
export function carrierChoice(value: string, settings: OrderFormSettings | undefined): Pick<LineState, "carrier" | "ownCarrierId"> {
  const own = value.startsWith("own:") ? settings?.ownCarriers?.find((row) => row.id === value.slice(4)) : undefined;
  return own ? { carrier: own.carrier, ownCarrierId: own.id } : { carrier: value as Carrier, ownCarrierId: null };
}

export function lineMeters(line: LineState, settings: OrderFormSettings | undefined): number | null {
  const rule = carrierRule(line, settings);
  const count = readInt(line.count) ?? 0;
  if (rule?.loadingMeters != null) return Math.round(rule.loadingMeters * count * 100) / 100;
  return readDecimal(line.loadingMeters);
}

export function totalsOf(lines: LineState[], settings: OrderFormSettings | undefined): Totals {
  let pallets = 0;
  let weight = 0;
  let weightKnown = false;
  let metres = 0;
  let metresKnown = false;
  for (const line of lines) {
    const count = readInt(line.count) ?? 0;
    if (carrierRule(line, settings)?.palletPlace) pallets += count;
    const kg = readInt(line.weightKg);
    if (kg !== null) {
      weight += kg;
      weightKnown = true;
    }
    const lm = lineMeters(line, settings);
    if (lm !== null) {
      metres += lm;
      metresKnown = true;
    }
  }
  return { pallets, weightKg: weightKnown ? weight : null, loadingMeters: metresKnown ? Math.round(metres * 100) / 100 : null };
}

// ─── The form as WLX takes it ──────────────────────────────────────────────────────────────────

export function addressBody(address: AddressState) {
  const text = (value: string) => value.trim() || null;
  return {
    name: address.name.trim(),
    street: text(address.street),
    houseNumber: text(address.houseNumber),
    houseNumberAddition: text(address.houseNumberAddition),
    postalCode: text(address.postalCode),
    city: address.city.trim(),
    countryCode: address.countryCode,
  };
}

export function stopBody(stop: StopState) {
  return {
    ...(stop.locationId ? { locationId: stop.locationId } : { address: addressBody(stop.address) }),
    date: stop.date,
    ...(stop.from ? { from: stop.from } : {}),
    ...(stop.to ? { to: stop.to } : {}),
    ...(endsBeforeStart(stop) && stop.toNextDay ? { toNextDay: true } : {}),
    reference: stop.reference.trim() || null,
    instruction: stop.instruction.trim() || null,
    contactName: stop.contactName.trim() || null,
    contactPhone: stop.contactPhone.trim() || null,
    requirements: stop.requirement ? [stop.requirement] : [],
  };
}

/**
 * A line as WLX takes it, of a transport order or of a warehouse order's transport. An own carrier goes by its id
 * while the customer still has it (only then: an older API knows no such field), with the fixed carrier it counts as.
 */
export function lineBody(line: LineState, settings: OrderFormSettings | undefined) {
  const own = ownCarrierOf(line, settings);
  const fixed = carrierRule(line, settings)?.loadingMeters != null;
  return {
    count: readInt(line.count) ?? 1,
    carrier: own?.carrier ?? line.carrier,
    ...(own ? { ownCarrierId: own.id } : {}),
    description: line.description.trim(),
    weightKg: readInt(line.weightKg),
    loadingMeters: fixed ? null : readDecimal(line.loadingMeters),
  };
}

/** The order as POST /partner/orders takes it; only for a form whose steps are complete. */
export function orderBody(form: FormState, settings: OrderFormSettings | undefined) {
  return {
    customerReference: form.customerReference.trim(),
    consignments: form.consignments.map((c) => ({
      load: stopBody(c.load),
      unload: stopBody(c.unload),
      lines: c.lines.map((line) => lineBody(line, settings)),
      requirementIds: c.requirementIds,
      volumeM3: readDecimal(c.volumeM3 ?? ""),
      adrLines: adrLinesToSend(c.requirementIds, c.adrLines, settings),
      sender: c.sender ? addressBody(c.sender) : null,
      consignee: c.consignee ? addressBody(c.consignee) : null,
      ...((c.packingSlips ?? []).length > 0 ? { packingSlipIds: c.packingSlips!.map((slip) => slip.id) } : {}),
    })),
  };
}

/** What the checks can read while typing: a stop with its place and day, whatever else is missing. */
export function previewBody(form: FormState, editing: string | null) {
  const stop = (s: StopState) => (stopPlaced(s) && s.date ? stopBody(s) : null);
  return {
    customerReference: form.customerReference.trim() || undefined,
    ...(editing ? { order: editing } : {}),
    consignments: form.consignments.map((c) => ({ load: stop(c.load), unload: stop(c.unload) })),
  };
}

export interface StopFindings {
  known: boolean;
  notes: { code: "outside_hours" | "closed_that_day" | "window_in_past"; hours?: string }[];
}

export interface Preview {
  outcome: "confirmed" | "waiting";
  waitingReasons: WaitingReason[];
  referenceTaken: string | null;
  cutoff: { time: string; loadDate: string; until: string; late: boolean } | null;
  consignments: ({ load: StopFindings; unload: StopFindings; blocks: boolean } | null)[];
}

export function preview(form: FormState, editing: string | null) {
  return call<Preview>("POST", "/partner/orders/preview", previewBody(form, editing));
}

export interface Sent {
  number: string;
  status: "confirmed" | "waiting";
  waitingReasons: WaitingReason[];
  receivedAt: string;
  consignments: { number: string }[];
}

export function sendOrder(form: FormState, settings: OrderFormSettings | undefined, key: string) {
  return call<Sent>("POST", "/partner/orders", orderBody(form, settings), { "X-Idempotency-Key": key });
}

export function changeOrder(number: string, form: FormState, settings: OrderFormSettings | undefined) {
  return call<Sent>("PUT", `/partner/orders/${encodeURIComponent(number)}`, orderBody(form, settings));
}

/** A packing slip to WLX, which keeps it until the order is sent with its id. */
export function uploadPackingSlip(file: File) {
  return send<PackingSlip>("POST", `/partner/order-files?name=${encodeURIComponent(file.name)}`, file, { "Content-Type": "application/pdf" });
}

/** Taken off again, before the order is sent. */
export function removePackingSlip(id: string) {
  return call<null>("DELETE", `/partner/order-files/${encodeURIComponent(id)}`);
}

export function cancelOrder(number: string, reason: string) {
  return call<{ number: string; status: "cancelled" }>("POST", `/partner/orders/${encodeURIComponent(number)}/cancel`, { reason });
}

// ─── An order again ────────────────────────────────────────────────────────────────────────────

interface FormStop {
  locationId: string | null;
  address: { name: string; street: string | null; houseNumber: string | null; houseNumberAddition: string | null; postalCode: string | null; city: string; countryCode: string };
  date: string;
  from: string;
  to: string;
  /** A window that runs to the next day (P82); absent from an older API. */
  toNextDay?: boolean;
  reference: string | null;
  instruction: string | null;
  contactName?: string | null;
  contactPhone?: string | null;
  requirements: StopRequirement[];
}

interface FormLine {
  count: number;
  carrier: Carrier;
  description: string;
  weightKg: number | null;
  loadingMeters: number | null;
  /** A carrier of the customer's own, and its name when the order was sent (§19). */
  ownCarrierId?: string;
  carrierName?: string;
}

type FormAddress = FormStop["address"];

export interface OrderAsForm {
  number: string;
  customerReference: string | null;
  canChange: boolean;
  notChangeableBecause: NotChangeable | null;
  consignments: {
    number: string;
    load: FormStop | null;
    unload: FormStop | null;
    lines: FormLine[];
    requirementIds: string[];
    volumeM3?: number | null;
    adrLines?: { code: string; packingGroup: string | null; quantity: number | null; unit: "kg" | "l" }[];
    sender: FormAddress | null;
    consignee: FormAddress | null;
  }[];
}

export function orderAsFormQuery(number: string) {
  return queryOptions({
    queryKey: ["partner", "order-form", number],
    queryFn: () => call<OrderAsForm>("GET", `/partner/orders/${encodeURIComponent(number)}/form`),
    staleTime: 0,
  });
}

function addressState(address: FormAddress | null | undefined): AddressState {
  if (!address) return emptyAddress();
  return {
    name: address.name,
    street: address.street ?? "",
    houseNumber: address.houseNumber ?? "",
    houseNumberAddition: address.houseNumberAddition ?? "",
    postalCode: address.postalCode ?? "",
    city: address.city,
    countryCode: address.countryCode,
  };
}

function stopState(stop: FormStop | null, keepDays: boolean): StopState {
  if (!stop) return emptyStop();
  return {
    locationId: stop.locationId,
    address: addressState(stop.address),
    typing: stop.locationId === null,
    date: keepDays ? stop.date : "",
    from: stop.from,
    to: stop.to,
    // An older API says it only by the times: an end before the start was always the next day.
    toNextDay: stop.toNextDay ?? stop.to < stop.from,
    reference: keepDays ? (stop.reference ?? "") : "",
    instruction: stop.instruction ?? "",
    contactName: stop.contactName ?? "",
    contactPhone: stop.contactPhone ?? "",
    requirement: stop.requirements[0] ?? "",
  };
}

/**
 * The form filled in from an order: as it is, to change it (Wijzigen); or to start a new one from it (Herhalen,
 * Opnieuw invoeren) — then the days, the references and your reference are the customer's to fill in (H-o06).
 */
export function formFromOrder(order: OrderAsForm, mode: "edit" | "copy"): FormState {
  const keep = mode === "edit";
  return {
    version: 1,
    customerReference: keep ? (order.customerReference ?? "") : "",
    consignments: order.consignments.map((c) => ({
      key: newKey(),
      load: stopState(c.load, keep),
      unload: stopState(c.unload, keep),
      lines:
        c.lines.length > 0
          ? c.lines.map((line) => ({
              count: String(line.count),
              carrier: line.carrier,
              // Picked again while the customer still has it; else the line is the carrier it counted as.
              ...(line.ownCarrierId ? { ownCarrierId: line.ownCarrierId } : {}),
              description: line.description,
              weightKg: line.weightKg === null ? "" : String(line.weightKg),
              loadingMeters: line.loadingMeters === null ? "" : String(line.loadingMeters).replace(".", ","),
            }))
          : [emptyLine()],
      requirementIds: c.requirementIds,
      volumeM3: c.volumeM3 == null ? "" : String(c.volumeM3).replace(".", ","),
      adrLines: (c.adrLines ?? []).map((line) => ({
        code: line.code,
        packingGroup: line.packingGroup ?? "",
        quantity: line.quantity === null ? "" : String(line.quantity).replace(".", ","),
        unit: line.unit,
      })),
      sender: c.sender ? addressState(c.sender) : null,
      consignee: c.consignee ? addressState(c.consignee) : null,
      // A packing slip stays with the order it was sent with: changing one or starting from it starts without.
      packingSlips: [],
    })),
  };
}

// ─── The concept ───────────────────────────────────────────────────────────────────────────────

export const draftQuery = queryOptions({
  queryKey: ["partner", "order-draft"],
  queryFn: () => call<{ payload: unknown; savedAt: string } | null>("GET", "/partner/order-draft"),
  staleTime: 0,
});

export function saveDraft(payload: DraftPayload) {
  return call<{ savedAt: string }>("PUT", "/partner/order-draft", { payload });
}

export function dropDraft() {
  return call<void>("DELETE", "/partner/order-draft");
}
